<template>
  <div class="lp-root">

    <!-- SIDEBAR -->
    <aside class="lp-sidebar">
      <div class="lp-sidebar-inner">

        <div class="lp-brand">
          <div class="lp-brand-card">
            <img src="../assets/uvoto.png" alt="Uvoto" class="lp-logo-uvoto" />
            <span class="lp-brand-sep" aria-hidden="true"></span>
            <img src="../assets/uvoto-branding.png" alt="Uvoto Branding" class="lp-logo-branding" />
          </div>
        </div>

        <nav class="lp-nav">
          <a
            href="#privacy-policy"
            :class="['lp-nav-item', { active: active === 'privacy-policy' }]"
            @click.prevent="scrollTo('privacy-policy')"
          >{{ lang === 'es' ? 'Política de Privacidad' : 'Privacy Policy' }}</a>
          <a
            href="#terms"
            :class="['lp-nav-item', { active: active === 'terms' }]"
            @click.prevent="scrollTo('terms')"
          >Terms of Service</a>
        </nav>


        <div class="lp-sidebar-footer">
          <a href="https://uvoto.com" class="lp-footer-link">uvoto.com</a>
          <a href="mailto:support@uvoto.com" class="lp-footer-link">support@uvoto.com</a>
          <span class="lp-footer-copy">© 2026 Uvoto. All rights reserved.</span>
        </div>

      </div>
    </aside>

    <!-- DOCUMENT AREA -->
    <main class="lp-main" ref="main" @scroll="onScroll">

      <!-- PRIVACY POLICY -->
      <article id="privacy-policy" class="lp-doc">
        <div class="lp-lang" role="group" aria-label="Idioma / Language">
          <button type="button" :class="{ 'is-on': lang === 'es' }" @click="setLang('es')">Español</button>
          <button type="button" :class="{ 'is-on': lang === 'en' }" @click="setLang('en')">English</button>
        </div>

        <template v-if="lang === 'es'">
          <div class="lp-doc-header">
            <h1 class="lp-doc-title">Política de Privacidad</h1>
            <p class="lp-doc-meta">Uvoto &nbsp;·&nbsp; Uvoto Audience Engagement y Uvoto Branding &nbsp;·&nbsp; Vigente desde: septiembre de 2026</p>
          </div>

          <p>En Uvoto tratamos la información con el mismo cuidado con el que construimos el producto. Esta Política explica qué datos tratamos, para qué, con quién los compartimos, cuánto tiempo los conservamos y cómo puedes ejercer tus derechos. Aplica a todos los productos y servicios de Uvoto y está diseñada para cumplir la Ley 1581 de 2012 de Colombia y sus decretos reglamentarios, y para respetar los estándares internacionales de protección de datos.</p>

          <div class="lp-summary-box">
            <h4>Lo esencial</h4>
            <ul class="lp-list">
              <li>No vendemos ni alquilamos datos personales.</li>
              <li>El contenido con el que entrenas a tu asistente solo se usa para tu asistente, nunca para el de otra marca.</li>
              <li>Las integraciones, como Instagram, solo se activan si tú las conectas, y puedes desconectarlas cuando quieras.</li>
              <li>Puedes acceder, corregir y eliminar tus datos escribiendo a support@uvoto.com.</li>
            </ul>
          </div>

          <h2>1. Quiénes somos y alcance</h2>
          <p><strong>Uvoto</strong> («Uvoto», «nosotros») es el responsable del tratamiento de los datos personales descritos en esta Política. Nuestros datos de contacto son: correo <a href="mailto:support@uvoto.com">support@uvoto.com</a>, sitio web <a href="https://uvoto.com" target="_blank" rel="noopener">uvoto.com</a>, Colombia.</p>
          <p>Esta Política cubre:</p>
          <ul class="lp-list">
            <li><strong>Uvoto Audience Engagement:</strong> herramientas de interacción en tiempo real para eventos, clases y sesiones (preguntas, votaciones, proyección y paneles).</li>
            <li><strong>Uvoto Branding:</strong> páginas de marca interactivas publicadas en uvoto.com y sus enlaces cortos, la sala de espera con lienzos, el asistente de inteligencia artificial de cada marca, el Training Center, la agenda y las integraciones con otras plataformas.</li>
            <li>Las aplicaciones móviles de Uvoto para iOS y Android, el sitio web uvoto.com y cualquier servicio que remita a esta Política.</li>
          </ul>

          <h2>2. Nuestro papel frente a los datos</h2>
          <p>Uvoto actúa en dos calidades distintas, y es importante diferenciarlas:</p>
          <h3>Como responsable</h3>
          <p>Frente a los datos de las personas que crean una cuenta en Uvoto (dueños de marca, creadores, organizadores y administradores): datos de cuenta, membresía, pagos, soporte, seguridad y uso del servicio.</p>
          <h3>Como encargado</h3>
          <p>Frente a los datos que la audiencia de una marca comparte en la página de esa marca o con su asistente (por ejemplo, mensajes, nombre o teléfono para agendar una cita), y frente al contenido que el dueño de la marca carga para entrenar su asistente. En esos casos tratamos los datos por cuenta del dueño de la marca y conforme a sus instrucciones. El dueño de la marca es el responsable frente a su audiencia y debe cumplir las obligaciones descritas en la sección 11.</p>

          <h2>3. Información que tratamos</h2>
          <h3>3.1 Datos de cuenta</h3>
          <p>Nombre, correo electrónico, foto de perfil e identificadores que recibimos del proveedor de inicio de sesión que elijas (por ejemplo, Google o Apple), preferencias de idioma y configuración de la cuenta.</p>
          <h3>3.2 Contenido de marca y de entrenamiento</h3>
          <p>Nombre y logo de la marca, imágenes, videos, textos, productos, servicios, precios, horarios, enlaces, archivos, reglas de comportamiento y los mensajes que escribes en el chat de entrenamiento del Training Center.</p>
          <h3>3.3 Interacciones de la audiencia</h3>
          <p>Mensajes y preguntas enviados al asistente de una marca, votos, reacciones y los datos que un visitante escribe voluntariamente, como su nombre o teléfono al agendar. Los visitantes pueden usar una página de marca sin crear cuenta; en ese caso usamos un identificador anónimo de sesión.</p>
          <h3>3.4 Integraciones con otras plataformas</h3>
          <p>Solo si decides conectarlas: datos de tu cuenta profesional de Instagram (detallados en la sección 6) y el contenido público de enlaces que eliges entrenar, como canales de YouTube, páginas de Facebook o sitios web.</p>
          <h3>3.5 Pagos y membresías</h3>
          <p>Las compras se procesan a través de App Store (Apple) y Google Play. Uvoto no recibe ni almacena los datos completos de tu tarjeta; recibimos la confirmación de la compra, el producto adquirido, su estado y sus fechas de vigencia.</p>
          <h3>3.6 Datos técnicos y de uso</h3>
          <p>Tipo de dispositivo, sistema operativo, idioma, dirección IP aproximada, identificadores para notificaciones, registros de errores y métricas de uso, como visitas a una página de marca o clics en sus enlaces.</p>

          <h2>4. Para qué usamos la información</h2>
          <ul class="lp-list">
            <li>Prestar el servicio: crear y publicar páginas de marca, habilitar la interacción con la audiencia y generar las respuestas del asistente.</li>
            <li>Entrenar el asistente de <strong>tu</strong> marca con el contenido que tú cargas o conectas.</li>
            <li>Mostrar al dueño de cada marca las interacciones y métricas de su propia audiencia.</li>
            <li>Gestionar membresías, periodos de prueba y acceso a funciones.</li>
            <li>Enviar notificaciones relacionadas con el servicio, como nuevas preguntas o agendamientos.</li>
            <li>Proteger la plataforma: prevenir fraude, abuso, spam y accesos no autorizados.</li>
            <li>Brindar soporte, mejorar la estabilidad del producto y cumplir obligaciones legales.</li>
          </ul>
          <p><strong>Lo que no hacemos:</strong> no vendemos ni alquilamos datos personales; no usamos el contenido de una marca para entrenar el asistente de otra marca; y no usamos los datos de la audiencia de una marca para publicidad propia de Uvoto.</p>

          <h2>5. Inteligencia artificial</h2>
          <p>El asistente de cada marca genera sus respuestas con modelos de inteligencia artificial de terceros, actualmente Google Gemini, a partir del contenido que el dueño de la marca le enseñó y del mensaje recibido. Para generar cada respuesta, el contenido necesario se envía al proveedor, que lo procesa conforme a sus propios términos para desarrolladores.</p>
          <ul class="lp-list">
            <li>Las respuestas de IA pueden contener errores o información incompleta. Recomendamos verificar directamente con la marca los datos importantes, como precios, disponibilidad o condiciones.</li>
            <li>El dueño de la marca puede revisar, corregir o eliminar en cualquier momento lo que su asistente aprendió, y definir reglas sobre cómo debe responder.</li>
            <li>No tomamos decisiones que produzcan efectos jurídicos o significativos sobre las personas basadas únicamente en tratamiento automatizado.</li>
          </ul>

          <h2>6. Datos de Instagram y de plataformas de Meta</h2>
          <p>Esta sección aplica solo cuando el titular de una cuenta profesional de Instagram decide conectarla desde Uvoto y autoriza los permisos solicitados en la página oficial de Instagram. Uvoto nunca conoce tu contraseña de Instagram.</p>
          <h3>Qué datos tratamos</h3>
          <ul class="lp-list">
            <li>Perfil básico: identificador, nombre de usuario, nombre, foto de perfil y tipo de cuenta.</li>
            <li>Tus propias publicaciones: texto, miniatura, enlace y, únicamente para los videos que elijas entrenar, el archivo de video.</li>
            <li>Comentarios en tus publicaciones y mensajes directos recibidos por tu cuenta, en la medida necesaria para las funciones que actives.</li>
          </ul>
          <h3>Para qué</h3>
          <p>Exclusivamente para que tu asistente responda los mensajes directos de tu cuenta, envíe un mensaje privado a quien comente según los disparadores que tú configures, y aprenda del contenido de los videos que elijas. No usamos estos datos para publicidad, no los vendemos y no los compartimos con terceros distintos de los proveedores de infraestructura necesarios para operar el servicio.</p>
          <h3>Cómo los protegemos y cuánto los conservamos</h3>
          <ul class="lp-list">
            <li>El token de acceso se guarda solo en nuestros servidores, con acceso restringido y cifrado en reposo; nunca se almacena en tu dispositivo.</li>
            <li>Los archivos de video se descargan de forma temporal para extraer su transcripción y resumen, y se eliminan al terminar el procesamiento. Conservamos la transcripción, el resumen, la miniatura y el enlace mientras el video siga entrenado.</li>
            <li>Puedes quitar cualquier video entrenado, y ese contenido deja de formar parte del conocimiento de tu asistente.</li>
          </ul>
          <h3>Cómo revocar el acceso</h3>
          <p>Desde la pestaña Instagram del Training Center, con la opción Desconectar, o desde Instagram en Configuración › Apps y sitios web. Al desconectar eliminamos el token de acceso. Si eliminas Uvoto desde Instagram o solicitas la eliminación de tus datos, Meta nos lo notifica y procesamos la solicitud automáticamente. También puedes escribirnos a support@uvoto.com.</p>
          <p>El uso de datos de Instagram se rige además por las Condiciones de la Plataforma y las Políticas para Desarrolladores de Meta, que Uvoto se compromete a cumplir.</p>

          <h2>7. Con quién compartimos información</h2>
          <p>Compartimos datos solo en la medida necesaria y con proveedores obligados contractualmente a protegerlos:</p>
          <ul class="lp-list">
            <li><strong>Google:</strong> Firebase y Google Cloud (autenticación, base de datos, almacenamiento y ejecución de funciones) y la API de Gemini (inteligencia artificial).</li>
            <li><strong>Meta:</strong> API de Instagram, solo si conectas tu cuenta.</li>
            <li><strong>Apple y Google:</strong> procesamiento de compras y envío de notificaciones.</li>
            <li><strong>Cloudflare y nuestro proveedor de alojamiento web:</strong> entrega y seguridad del sitio uvoto.com.</li>
          </ul>
          <p>Además, el dueño de cada marca puede ver las interacciones de su propia audiencia. Podremos revelar información cuando lo exija la ley o una autoridad competente, o para proteger los derechos y la seguridad de las personas y del servicio. Si Uvoto participa en una fusión, adquisición o venta de activos, la información podrá transferirse bajo garantías equivalentes a las de esta Política, con aviso previo.</p>

          <h2>8. Transferencias internacionales</h2>
          <p>Algunos de nuestros proveedores tratan datos en servidores ubicados fuera de Colombia, principalmente en Estados Unidos. Realizamos estas transferencias con proveedores que ofrecen garantías adecuadas de protección y conforme a lo exigido por la normativa colombiana de protección de datos.</p>

          <h2>9. Conservación</h2>
          <ul class="lp-list">
            <li><strong>Datos de cuenta:</strong> mientras la cuenta esté activa. Tras una solicitud de eliminación, los borramos o anonimizamos sin demora injustificada, salvo lo que debamos conservar por obligación legal.</li>
            <li><strong>Interacciones de la audiencia:</strong> mientras el dueño de la marca mantenga su canal o hasta que las elimine.</li>
            <li><strong>Tokens de integraciones:</strong> hasta que desconectes la integración o revoques el acceso.</li>
            <li><strong>Archivos de video de Instagram:</strong> solo durante su procesamiento.</li>
            <li><strong>Registros técnicos y de seguridad:</strong> por un periodo limitado, necesario para operar y proteger el servicio.</li>
            <li><strong>Registros de compras:</strong> el tiempo que exijan las normas contables y tributarias.</li>
          </ul>

          <h2>10. Seguridad</h2>
          <p>Aplicamos medidas técnicas y organizativas razonables y proporcionales, entre ellas: cifrado en tránsito (HTTPS/TLS); cifrado en reposo en la infraestructura de nuestros proveedores; reglas de acceso por cuenta, de modo que cada dueño solo accede a sus propios canales; almacenamiento de credenciales y claves en gestores de secretos; verificación criptográfica de las notificaciones que recibimos de plataformas externas; y acceso interno restringido según necesidad. Ningún sistema es infalible; si ocurre un incidente que afecte tus datos, lo gestionaremos y notificaremos conforme a la ley.</p>

          <h2>11. Responsabilidades del dueño de la marca</h2>
          <p>Quien crea y administra una marca en Uvoto se compromete a:</p>
          <ul class="lp-list">
            <li>Tener los derechos sobre el contenido que publica y con el que entrena a su asistente.</li>
            <li>No cargar datos sensibles (por ejemplo, de salud, biométricos, de orientación política o religiosa) ni datos de menores de edad sin la base legal correspondiente.</li>
            <li>Informar a su audiencia y contar con su autorización cuando recopile datos personales a través de su página o asistente, como sucede en agendamientos o formularios.</li>
            <li>Revisar lo que su asistente aprende y responde, y corregirlo cuando sea necesario.</li>
            <li>Cumplir las normas de las plataformas que conecta, incluidas las políticas contra el spam de Instagram.</li>
          </ul>
          <p>Uvoto no es responsable del uso que el dueño de una marca haga, fuera de la plataforma, de los datos de su audiencia.</p>

          <h2>12. Tus derechos</h2>
          <p>Como titular de datos personales, y conforme a la Ley 1581 de 2012 y sus decretos reglamentarios, tienes derecho a: conocer, actualizar y rectificar tus datos; solicitar prueba de la autorización otorgada; ser informado sobre el uso que se les da; revocar la autorización y solicitar la supresión de tus datos cuando no exista un deber legal de conservarlos; acceder a ellos de forma gratuita; y presentar quejas ante la Superintendencia de Industria y Comercio (SIC).</p>
          <p>Si te encuentras en otras jurisdicciones, también reconocemos los derechos que te otorgue la ley aplicable, como los derechos de acceso, rectificación, supresión, limitación, portabilidad y oposición del Reglamento General de Protección de Datos (RGPD) de la Unión Europea, o los derechos a saber, eliminar y no ser discriminado de las leyes de privacidad de California.</p>
          <h3>Cómo ejercerlos</h3>
          <p>Escríbenos a <a href="mailto:support@uvoto.com">support@uvoto.com</a> con el asunto «Privacidad», indicando tu solicitud y un medio para verificar tu identidad. Atendemos las consultas en un máximo de diez (10) días hábiles y los reclamos en un máximo de quince (15) días hábiles, prorrogables en los términos de la ley. Si eres parte de la audiencia de una marca, también puedes dirigirte directamente a esa marca; te ayudaremos a canalizar la solicitud.</p>

          <h2>13. Eliminación de la cuenta</h2>
          <p>Puedes solicitar la eliminación de tu cuenta y de los datos asociados escribiendo a support@uvoto.com o mediante el proceso de eliminación disponible en la aplicación. Una vez verificada la solicitud, eliminaremos o anonimizaremos la información, salvo la que debamos conservar por obligación legal, seguridad o prevención de fraude.</p>

          <h2>14. Menores de edad</h2>
          <p>Uvoto no está dirigido a menores de 14 años y no recopilamos conscientemente sus datos personales. Si crees que un menor nos proporcionó datos sin la autorización de su representante legal, escríbenos y los eliminaremos.</p>

          <h2>15. Cookies y almacenamiento local</h2>
          <p>uvoto.com y nuestras aplicaciones usan almacenamiento local del navegador o del dispositivo para funciones esenciales, como mantener la sesión, recordar el idioma, guardar tus sesiones recientes y asignar el identificador anónimo de visitante. No usamos cookies de publicidad de terceros.</p>

          <h2>16. Cambios a esta Política</h2>
          <p>Podemos actualizar esta Política para reflejar cambios en el servicio o en la ley. Publicaremos la versión vigente en esta página con su fecha y, si los cambios son significativos, te avisaremos dentro de la aplicación o por correo electrónico.</p>

          <h2>17. Contacto</h2>
          <p>Uvoto &nbsp;·&nbsp; Colombia &nbsp;·&nbsp; <a href="mailto:support@uvoto.com">support@uvoto.com</a> &nbsp;·&nbsp; <a href="https://uvoto.com" target="_blank" rel="noopener">uvoto.com</a></p>

          <p class="lp-agreement">Al usar Uvoto Audience Engagement o Uvoto Branding, reconoces que has leído esta Política de Privacidad.</p>
        </template>

        <template v-else>
          <div class="lp-doc-header">
            <h1 class="lp-doc-title">Privacy Policy</h1>
            <p class="lp-doc-meta">Uvoto &nbsp;·&nbsp; Uvoto Audience Engagement and Uvoto Branding &nbsp;·&nbsp; Effective: September 2026</p>
          </div>

          <p>At Uvoto we treat information with the same care we put into building the product. This Policy explains what data we process, why, who we share it with, how long we keep it and how you can exercise your rights. It applies to all Uvoto products and services and is designed to comply with Colombia's Law 1581 of 2012 and its implementing regulations, while respecting international data protection standards.</p>

          <div class="lp-summary-box">
            <h4>The essentials</h4>
            <ul class="lp-list">
              <li>We don't sell or rent personal data.</li>
              <li>The content you use to train your assistant is only used for your assistant, never for another brand's.</li>
              <li>Integrations such as Instagram are only enabled if you connect them, and you can disconnect them at any time.</li>
              <li>You can access, correct and delete your data by writing to support@uvoto.com.</li>
            </ul>
          </div>

          <h2>1. Who we are and scope</h2>
          <p><strong>Uvoto</strong> ("Uvoto", "we") is the controller of the personal data described in this Policy. Contact: email <a href="mailto:support@uvoto.com">support@uvoto.com</a>, website <a href="https://uvoto.com" target="_blank" rel="noopener">uvoto.com</a>, Colombia.</p>
          <p>This Policy covers:</p>
          <ul class="lp-list">
            <li><strong>Uvoto Audience Engagement:</strong> real-time interaction tools for events, classes and sessions (questions, polls, projection and panels).</li>
            <li><strong>Uvoto Branding:</strong> interactive brand pages published on uvoto.com and their short links, the waiting room with canvases, each brand's artificial intelligence assistant, the Training Center, scheduling and integrations with other platforms.</li>
            <li>Uvoto's mobile apps for iOS and Android, the uvoto.com website and any service that links to this Policy.</li>
          </ul>

          <h2>2. Our role regarding data</h2>
          <p>Uvoto acts in two different capacities, and the distinction matters:</p>
          <h3>As controller</h3>
          <p>For the data of people who create a Uvoto account (brand owners, creators, organizers and administrators): account data, membership, payments, support, security and service usage.</p>
          <h3>As processor</h3>
          <p>For the data a brand's audience shares on that brand's page or with its assistant (for example, messages, or a name or phone number to book an appointment), and for the content a brand owner uploads to train their assistant. In those cases we process data on behalf of, and under the instructions of, the brand owner, who is the controller towards their audience and must meet the obligations described in section 11.</p>

          <h2>3. Information we process</h2>
          <h3>3.1 Account data</h3>
          <p>Name, email address, profile photo and identifiers received from the sign-in provider you choose (for example, Google or Apple), language preferences and account settings.</p>
          <h3>3.2 Brand and training content</h3>
          <p>Brand name and logo, images, videos, texts, products, services, prices, business hours, links, files, behavior rules and the messages you write in the Training Center chat.</p>
          <h3>3.3 Audience interactions</h3>
          <p>Messages and questions sent to a brand's assistant, votes, reactions, and data a visitor voluntarily enters, such as their name or phone number when booking. Visitors can use a brand page without an account; in that case we use an anonymous session identifier.</p>
          <h3>3.4 Integrations with other platforms</h3>
          <p>Only if you choose to connect them: data from your Instagram professional account (detailed in section 6) and the public content of links you choose to train, such as YouTube channels, Facebook pages or websites.</p>
          <h3>3.5 Payments and memberships</h3>
          <p>Purchases are processed through the App Store (Apple) and Google Play. Uvoto does not receive or store your full card details; we receive the purchase confirmation, the product, its status and its validity dates.</p>
          <h3>3.6 Technical and usage data</h3>
          <p>Device type, operating system, language, approximate IP address, notification identifiers, error logs and usage metrics, such as visits to a brand page or clicks on its links.</p>

          <h2>4. How we use information</h2>
          <ul class="lp-list">
            <li>Provide the service: create and publish brand pages, enable audience interaction and generate the assistant's responses.</li>
            <li>Train <strong>your</strong> brand's assistant with the content you upload or connect.</li>
            <li>Show each brand owner the interactions and metrics of their own audience.</li>
            <li>Manage memberships, trial periods and feature access.</li>
            <li>Send service-related notifications, such as new questions or bookings.</li>
            <li>Protect the platform: prevent fraud, abuse, spam and unauthorized access.</li>
            <li>Provide support, improve product stability and comply with legal obligations.</li>
          </ul>
          <p><strong>What we don't do:</strong> we don't sell or rent personal data; we don't use one brand's content to train another brand's assistant; and we don't use a brand's audience data for Uvoto's own advertising.</p>

          <h2>5. Artificial intelligence</h2>
          <p>Each brand's assistant generates responses using third-party artificial intelligence models, currently Google Gemini, based on the content the brand owner taught it and the message received. To generate each response, the necessary content is sent to the provider, which processes it under its own developer terms.</p>
          <ul class="lp-list">
            <li>AI responses may contain errors or incomplete information. We recommend confirming important details, such as prices, availability or conditions, directly with the brand.</li>
            <li>Brand owners can review, correct or delete what their assistant has learned at any time, and set rules on how it should respond.</li>
            <li>We don't make decisions that produce legal or similarly significant effects on people based solely on automated processing.</li>
          </ul>

          <h2>6. Instagram and Meta platform data</h2>
          <p>This section applies only when the owner of an Instagram professional account chooses to connect it from Uvoto and grants the requested permissions on Instagram's official page. Uvoto never sees your Instagram password.</p>
          <h3>What data we process</h3>
          <ul class="lp-list">
            <li>Basic profile: identifier, username, name, profile picture and account type.</li>
            <li>Your own posts: caption, thumbnail, link and, only for videos you choose to train, the video file.</li>
            <li>Comments on your posts and direct messages received by your account, to the extent needed for the features you enable.</li>
          </ul>
          <h3>Why</h3>
          <p>Solely so your assistant can reply to your account's direct messages, send a private message to people who comment according to the triggers you configure, and learn from the content of the videos you select. We don't use this data for advertising, we don't sell it, and we don't share it with third parties other than the infrastructure providers needed to operate the service.</p>
          <h3>How we protect it and how long we keep it</h3>
          <ul class="lp-list">
            <li>The access token is stored only on our servers, with restricted access and encryption at rest; it is never stored on your device.</li>
            <li>Video files are downloaded temporarily to extract their transcript and summary, and are deleted once processing finishes. We keep the transcript, summary, thumbnail and link for as long as the video remains trained.</li>
            <li>You can remove any trained video, and that content stops being part of your assistant's knowledge.</li>
          </ul>
          <h3>How to revoke access</h3>
          <p>From the Instagram tab in the Training Center using Disconnect, or from Instagram under Settings › Apps and websites. When you disconnect, we delete the access token. If you remove Uvoto from Instagram or request deletion of your data, Meta notifies us and we process the request automatically. You can also write to support@uvoto.com.</p>
          <p>The use of Instagram data is also governed by Meta's Platform Terms and Developer Policies, which Uvoto is committed to complying with.</p>

          <h2>7. Who we share information with</h2>
          <p>We share data only to the extent necessary and with providers contractually bound to protect it:</p>
          <ul class="lp-list">
            <li><strong>Google:</strong> Firebase and Google Cloud (authentication, database, storage and function execution) and the Gemini API (artificial intelligence).</li>
            <li><strong>Meta:</strong> Instagram API, only if you connect your account.</li>
            <li><strong>Apple and Google:</strong> purchase processing and push notifications.</li>
            <li><strong>Cloudflare and our web hosting provider:</strong> delivery and security of uvoto.com.</li>
          </ul>
          <p>In addition, each brand owner can see their own audience's interactions. We may disclose information when required by law or a competent authority, or to protect the rights and safety of people and the service. If Uvoto is involved in a merger, acquisition or asset sale, information may be transferred under safeguards equivalent to this Policy, with prior notice.</p>

          <h2>8. International transfers</h2>
          <p>Some of our providers process data on servers located outside Colombia, mainly in the United States. We carry out these transfers with providers that offer adequate protection safeguards and in accordance with Colombian data protection law.</p>

          <h2>9. Retention</h2>
          <ul class="lp-list">
            <li><strong>Account data:</strong> for as long as the account is active. After a deletion request, we delete or anonymize it without undue delay, except for what we must keep by law.</li>
            <li><strong>Audience interactions:</strong> for as long as the brand owner keeps their channel or until they delete them.</li>
            <li><strong>Integration tokens:</strong> until you disconnect the integration or revoke access.</li>
            <li><strong>Instagram video files:</strong> only while they are being processed.</li>
            <li><strong>Technical and security logs:</strong> for a limited period needed to operate and protect the service.</li>
            <li><strong>Purchase records:</strong> for as long as accounting and tax rules require.</li>
          </ul>

          <h2>10. Security</h2>
          <p>We apply reasonable and proportionate technical and organizational measures, including: encryption in transit (HTTPS/TLS); encryption at rest in our providers' infrastructure; per-account access rules, so each owner can only access their own channels; storage of credentials and keys in secret managers; cryptographic verification of notifications received from external platforms; and need-to-know internal access. No system is infallible; if an incident affects your data, we will handle it and notify as required by law.</p>

          <h2>11. Brand owner responsibilities</h2>
          <p>Anyone who creates and manages a brand on Uvoto agrees to:</p>
          <ul class="lp-list">
            <li>Hold the rights to the content they publish and use to train their assistant.</li>
            <li>Not upload sensitive data (for example, health, biometric, political or religious data) or data about minors without the appropriate legal basis.</li>
            <li>Inform their audience and obtain their authorization when collecting personal data through their page or assistant, such as bookings or forms.</li>
            <li>Review what their assistant learns and responds, and correct it when necessary.</li>
            <li>Comply with the rules of the platforms they connect, including Instagram's anti-spam policies.</li>
          </ul>
          <p>Uvoto is not responsible for how a brand owner uses their audience's data outside the platform.</p>

          <h2>12. Your rights</h2>
          <p>As a data subject, and under Law 1581 of 2012 and its regulations, you have the right to: know, update and correct your data; request proof of the authorization granted; be informed about how it is used; revoke authorization and request deletion of your data when there is no legal duty to keep it; access it free of charge; and file complaints with Colombia's Superintendence of Industry and Commerce (SIC).</p>
          <p>If you are in other jurisdictions, we also honor the rights granted by applicable law, such as the rights of access, rectification, erasure, restriction, portability and objection under the EU General Data Protection Regulation (GDPR), or the rights to know, delete and non-discrimination under California privacy laws.</p>
          <h3>How to exercise them</h3>
          <p>Write to <a href="mailto:support@uvoto.com">support@uvoto.com</a> with the subject "Privacy", describing your request and a way to verify your identity. We answer inquiries within ten (10) business days and complaints within fifteen (15) business days, extendable as permitted by law. If you are part of a brand's audience, you can also contact that brand directly; we will help route your request.</p>

          <h2>13. Account deletion</h2>
          <p>You can request deletion of your account and associated data by writing to support@uvoto.com or through the deletion process available in the app. Once the request is verified, we will delete or anonymize the information, except what we must keep for legal obligations, security or fraud prevention.</p>

          <h2>14. Children</h2>
          <p>Uvoto is not directed to children under 14 and we do not knowingly collect their personal data. If you believe a child provided data without their legal guardian's authorization, contact us and we will delete it.</p>

          <h2>15. Cookies and local storage</h2>
          <p>uvoto.com and our apps use browser or device local storage for essential functions, such as keeping you signed in, remembering your language, saving your recent sessions and assigning the anonymous visitor identifier. We don't use third-party advertising cookies.</p>

          <h2>16. Changes to this Policy</h2>
          <p>We may update this Policy to reflect changes in the service or the law. We will publish the current version on this page with its date and, if changes are significant, notify you in the app or by email.</p>

          <h2>17. Contact</h2>
          <p>Uvoto &nbsp;·&nbsp; Colombia &nbsp;·&nbsp; <a href="mailto:support@uvoto.com">support@uvoto.com</a> &nbsp;·&nbsp; <a href="https://uvoto.com" target="_blank" rel="noopener">uvoto.com</a></p>

          <p class="lp-agreement">By using Uvoto Audience Engagement or Uvoto Branding, you acknowledge that you have read this Privacy Policy.</p>
        </template>
      </article>

      <div class="lp-section-divider"></div>

      <!-- TERMS OF SERVICE -->
      <article id="terms" class="lp-doc">

        <div class="lp-doc-header">
          <h1 class="lp-doc-title">Terms of Service</h1>
          <p class="lp-doc-meta">Uvoto Audience Engagement &nbsp;·&nbsp; Last Updated: June 13, 2026</p>
        </div>

        <p>Welcome to Uvoto Audience Engagement ("Uvoto," "we," "our," or "the Platform"). By accessing or using our services, you agree to be bound by these Terms of Service. If you do not agree with these terms, you must refrain from using the Platform.</p>

        <h2>1. Description of Service</h2>
        <p>Uvoto Audience Engagement is a platform designed to facilitate real-time interaction and participation between organizers, presenters, panelists, and attendees during events, conferences, meetings, classes, webinars, and digital or in-person experiences. Features may include live polls, Q&A sessions, voting, mobile participation, content sharing and projection, panelist and moderator management, collaboration tools, AI-powered features, and third-party integrations. Uvoto may modify, expand, or remove features at any time.</p>

        <h2>2. Eligibility</h2>
        <p>To use the services, the user declares that they have legal capacity to enter into contracts, will use the Platform in accordance with applicable laws, and that the information provided is accurate and up to date. If acting on behalf of a company or organization, the user declares having sufficient authority to accept these terms on behalf of that entity.</p>

        <h2>3. User Accounts</h2>
        <p>The user is responsible for maintaining the confidentiality of their credentials, all activities performed from their account, and for reporting any unauthorized access. Uvoto may suspend or cancel accounts when there are reasonable grounds to believe misuse of the Platform is occurring.</p>

        <h2>4. Plans and Service Access</h2>
        <p>Uvoto may offer free plans, monthly or annual subscriptions, enterprise services, and premium features. Prices, features, and limits of each plan may be modified at any time. Unless expressly stated, payments made are non-refundable.</p>

        <h2>5. User Content</h2>
        <p>Users retain ownership of the content they upload, share, or generate through the Platform. By using Uvoto, the user grants a limited, non-exclusive, worldwide license to host, process, and transmit such content solely for the purpose of providing the service. The user warrants that they hold all necessary rights over the content shared and that it does not infringe third-party rights.</p>

        <h2>6. Prohibited Conduct</h2>
        <p>The user agrees not to use the Platform for illegal activities; interfere with the security or operation of the service; attempt to access accounts or systems without authorization; distribute malware or malicious software; use bots, scraping, or unauthorized automated mechanisms; post offensive, fraudulent, or defamatory content; use the Platform to send spam or unsolicited communications; or resell the service without express authorization from Uvoto.</p>

        <h2>7. Panelist and Guest Participation</h2>
        <p>Event organizers may grant permissions to panelists, moderators, and other participants. The organizer is responsible for managing granted permissions, information shared during sessions, and the use made by invited users. Uvoto acts solely as a technology provider and does not control content generated by participants.</p>

        <h2>8. Artificial Intelligence</h2>
        <p>Some features may use artificial intelligence models. The user acknowledges that automatically generated responses may contain errors or inaccuracies, that information produced by AI systems does not constitute professional advice, and that the user is responsible for verifying any results before relying on them. Uvoto may use third-party AI providers to process certain requests.</p>

        <h2>9. Third-Party Services</h2>
        <p>The Platform may integrate with external services including Zoom, Google Meet, Microsoft Teams, YouTube, cloud storage services, AI tools, and authentication providers. Uvoto is not responsible for interruptions, changes, or failures caused by third parties.</p>

        <h2>10. Service Availability</h2>
        <p>Although Uvoto will endeavor to maintain high availability, it does not guarantee that the service will be uninterrupted or error-free. Uvoto will not be liable for losses arising from updates, scheduled maintenance, unforeseen interruptions, or failures of third-party providers.</p>

        <h2>11. Intellectual Property</h2>
        <p>All software, design, logos, trademarks, documentation, and other elements of Uvoto are the property of Uvoto or its licensors and are protected by applicable intellectual property laws. Nothing in these terms grants the user any ownership rights over the Platform.</p>

        <h2>12. Privacy and Data Protection</h2>
        <p>The processing of personal data is governed by the <a href="#privacy-policy" @click.prevent="scrollTo('privacy-policy')">Uvoto Privacy Policy</a>. By using the Platform, the user accepts the collection and processing of information necessary for the provision of the service.</p>

        <h2>13. Limitation of Liability</h2>
        <p>To the maximum extent permitted by law, Uvoto provides the service "as is" and "as available." It does not guarantee specific results and will not be liable for indirect, incidental, special, or consequential losses. Uvoto's total liability to the user will not exceed amounts actually paid by such user during the twelve (12) months preceding the event giving rise to the claim.</p>

        <h2>14. Indemnification</h2>
        <p>The user agrees to indemnify and hold harmless Uvoto, its officers, employees, and collaborators against claims arising from their use of the Platform, content shared by the user, breach of these terms, or infringement of third-party rights.</p>

        <h2>15. Suspension and Termination</h2>
        <p>Uvoto may suspend or cancel access to the Platform when fraudulent activity is detected, when there is a breach of these terms, when necessary to protect the security of the system, or when required by competent authorities. The user may stop using the service at any time.</p>

        <h2>16. Modifications</h2>
        <p>Uvoto may update these Terms periodically. Modifications will take effect from their publication on the Platform. Continued use of the service constitutes acceptance of such modifications.</p>

        <h2>17. Disclaimer of Warranties</h2>
        <p>Uvoto does not warrant that the service will be entirely free of errors, that results obtained will meet all user expectations, or that the Platform will be free of interruptions or vulnerabilities. The user uses the service at their own risk.</p>

        <h2>18. Governing Law and Dispute Resolution</h2>
        <p>These terms will be governed by the applicable laws of the country in which Uvoto has established its operating entity. The parties will endeavor to resolve any dispute through good faith negotiation before resorting to competent courts.</p>

        <h2>19. Severability</h2>
        <p>If any provision of these terms is declared invalid or unenforceable, the remaining provisions will continue in full force and effect.</p>

        <h2>20. Contact</h2>
        <p>For inquiries related to these Terms of Service, contact us at <a href="mailto:support@uvoto.com">support@uvoto.com</a> or visit <a href="https://uvoto.com" target="_blank">https://uvoto.com</a>.</p>

        <p class="lp-agreement">By using Uvoto Audience Engagement, you declare that you have read, understood, and agreed to these Terms of Service.</p>
      </article>

    </main>
  </div>
</template>

<script>
export default {
  name: 'PrivacyView',
  data() {
    return {
      active: 'privacy-policy',
      copied: null,
      lang: 'es'
    };
  },
  created() {
    let guardado = '';
    try { guardado = localStorage.getItem('uvoto_privacy_lang') || ''; } catch { /* sin almacenamiento */ }
    const pedido = new URLSearchParams(window.location.search).get('lang') || guardado;
    const navegador = (navigator.language || 'es').toLowerCase();
    this.lang = ['es', 'en'].includes(pedido) ? pedido : (navegador.startsWith('es') ? 'es' : 'en');
  },
  mounted() {
    const hash = window.location.hash?.replace('#', '');
    if (hash === 'terms' || hash === 'privacy-policy') {
      this.$nextTick(() => this.scrollTo(hash));
    }
  },
  methods: {
    setLang(lang) {
      this.lang = lang;
      try { localStorage.setItem('uvoto_privacy_lang', lang); } catch { /* sin almacenamiento */ }
    },
    scrollTo(id) {
      const el = document.getElementById(id);
      const main = this.$refs.main;
      if (el && main) {
        main.scrollTo({ top: el.offsetTop - 32, behavior: 'smooth' });
      }
      this.active = id;
      history.replaceState(null, '', '#' + id);
    },
    onScroll() {
      const main = this.$refs.main;
      ['privacy-policy', 'terms'].reverse().forEach(id => {
        const el = document.getElementById(id);
        if (el && main.scrollTop >= el.offsetTop - 120) {
          this.active = id;
        }
      });
    },
    async copy(url, key) {
      try {
        await navigator.clipboard.writeText(url);
        this.copied = key;
        setTimeout(() => { this.copied = null; }, 2000);
      } catch {}
    }
  }
};
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* ── ROOT ── */
.lp-root {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  color: #1c1e21;
}

/* ── SIDEBAR ── */
.lp-sidebar {
  width: 260px;
  min-width: 260px;
  height: 100vh;
  overflow-y: auto;
  border-right: 1px solid #dddfe2;
  background: #f0f2f5;
  display: flex;
  flex-direction: column;
}

.lp-sidebar-inner {
  padding: 32px 20px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.lp-brand {
  display: flex;
  align-items: center;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 1px solid #dddfe2;
}

.lp-logo {
  width: 52px;
  height: 52px;
  object-fit: contain;
  filter: brightness(0);
}

/* Nav */
.lp-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 28px;
}

.lp-nav-item {
  display: block;
  padding: 9px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #4b4f56;
  text-decoration: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.lp-nav-item:hover {
  background: #e4e6eb;
  color: #1c1e21;
}

.lp-nav-item.active {
  background: #1c1e21;
  color: #fff;
}

/* Direct links */
.lp-direct-links {
  padding: 20px 0;
  border-top: 1px solid #dddfe2;
  margin-bottom: auto;
}

.lp-direct-title {
  font-size: 11px;
  font-weight: 700;
  color: #8a8d91;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-bottom: 12px;
}

.lp-link-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.lp-link-label {
  font-size: 13px;
  color: #1c1e21;
}

.lp-copy-btn {
  font-size: 12px;
  font-weight: 600;
  color: #1877f2;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.lp-copy-btn:hover {
  text-decoration: underline;
}

/* Footer */
.lp-sidebar-footer {
  padding-top: 20px;
  border-top: 1px solid #dddfe2;
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.lp-footer-link {
  font-size: 12px;
  color: #4b4f56;
  text-decoration: none;
}

.lp-footer-link:hover { text-decoration: underline; }

.lp-footer-copy {
  font-size: 11px;
  color: #8a8d91;
  margin-top: 4px;
}

/* ── MAIN ── */
.lp-main {
  flex: 1;
  height: 100vh;
  overflow-y: auto;
  background: #fff;
}

/* ── DOCUMENT ── */
.lp-doc {
  max-width: 740px;
  padding: 56px 60px 72px;
}

.lp-doc-header {
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 2px solid #1c1e21;
}

.lp-doc-title {
  font-size: 26px;
  font-weight: 700;
  color: #1c1e21;
  letter-spacing: -0.3px;
  line-height: 1.2;
  margin-bottom: 6px;
}

.lp-doc-meta {
  font-size: 12.5px;
  color: #8a8d91;
}

.lp-doc p {
  font-size: 14.5px;
  line-height: 1.8;
  color: #1c1e21;
  text-align: justify;
  margin-bottom: 14px;
}

.lp-doc h2 {
  font-size: 15.5px;
  font-weight: 700;
  color: #1c1e21;
  margin-top: 32px;
  margin-bottom: 8px;
}

.lp-doc h3 {
  font-size: 14px;
  font-weight: 600;
  color: #4b4f56;
  margin-top: 18px;
  margin-bottom: 4px;
}

.lp-doc a {
  color: #1877f2;
  text-decoration: none;
}

.lp-doc a:hover {
  text-decoration: underline;
}

/* Summary box */
.lp-summary-box {
  background: #f0f2f5;
  border-left: 3px solid #1877f2;
  padding: 18px 22px;
  margin: 28px 0;
  border-radius: 0 6px 6px 0;
}

.lp-summary-box h4 {
  font-size: 13px;
  font-weight: 700;
  color: #1c1e21;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.lp-summary-box p {
  font-size: 13.5px;
  color: #4b4f56;
  text-align: left;
  margin: 0;
}

/* Agreement */
.lp-agreement {
  margin-top: 36px;
  padding-top: 20px;
  border-top: 1px solid #dddfe2;
  font-size: 12.5px !important;
  color: #8a8d91 !important;
  text-align: left !important;
}

/* Divider between docs */
.lp-section-divider {
  border-top: 8px solid #f0f2f5;
  margin: 0 60px;
}

/* ── MOBILE ── */
@media (max-width: 768px) {
  .lp-root {
    flex-direction: column;
    /* El shell de la app fija html/body/#app con overflow:hidden — esta
       vista debe seguir manejando su propio scroll interno (igual que en
       escritorio), no depender del scroll natural de la página. */
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
  }

  .lp-sidebar {
    width: 100%;
    min-width: 0;
    height: auto;
    flex-shrink: 0;
    border-right: none;
    border-bottom: 1px solid #dddfe2;
  }

  .lp-sidebar-inner {
    padding: 16px;
  }

  .lp-brand { margin-bottom: 14px; padding-bottom: 14px; }

  .lp-nav {
    flex-direction: row;
    gap: 6px;
    margin-bottom: 0;
  }

  .lp-nav-item {
    flex: 1;
    text-align: center;
    font-size: 13px;
  }

  .lp-direct-links,
  .lp-sidebar-footer {
    display: none;
  }

  .lp-main {
    flex: 1;
    min-height: 0;
    height: auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
  }

  .lp-doc {
    padding: 28px 20px 52px;
  }

  .lp-section-divider {
    margin: 0 20px;
  }
}

/* El shell de la app centra el texto: un documento legal se lee alineado. */
.lp-doc,
.lp-doc h1,
.lp-doc h2,
.lp-doc h3,
.lp-doc h4,
.lp-doc li,
.lp-sidebar-footer { text-align: left; }
.lp-doc { width: 100%; }

/* ── Marca: Uvoto + Uvoto Branding ── */
.lp-brand-card {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #0b0b0b;
}
.lp-logo-uvoto {
  height: 48px;
  width: auto;
  max-width: 45%;
  object-fit: contain;
}
.lp-brand-sep {
  width: 1px;
  align-self: stretch;
  background: rgba(255, 255, 255, 0.18);
}
.lp-logo-branding {
  height: 40px;
  width: auto;
  max-width: 45%;
  object-fit: contain;
}

/* ── Idioma ── */
.lp-lang {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  margin-bottom: 24px;
  border-radius: 999px;
  background: #f0f2f5;
}
.lp-lang button {
  border: none;
  background: transparent;
  padding: 7px 14px;
  border-radius: 999px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #4b4f56;
  cursor: pointer;
}
.lp-lang button.is-on {
  background: #1c1e21;
  color: #fff;
}

/* ── Listas ── */
.lp-list {
  margin: 0 0 14px;
  padding-left: 20px;
}
.lp-list li {
  font-size: 14.5px;
  line-height: 1.75;
  color: #1c1e21;
  margin-bottom: 6px;
}
.lp-summary-box .lp-list { margin: 0; }
.lp-summary-box .lp-list li { font-size: 13.5px; color: #4b4f56; margin-bottom: 4px; }

@media (max-width: 768px) {
  .lp-brand-card { padding: 10px 14px; }
  .lp-logo-uvoto { height: 36px; }
  .lp-logo-branding { height: 30px; }
}
</style>
